import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { SnackbarProvider } from 'notistack';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SettingsProvider, useSettings } from './context/SettingsContext';
import createAppTheme from './theme';
import Layout from './components/Layout';

// Pages
import Login               from './pages/Login';
import Dashboard           from './pages/Dashboard';
import Students            from './pages/Students';
import Teachers            from './pages/Teachers';
import Subjects            from './pages/Subjects';
import Enrolments          from './pages/Enrolments';
import Assessments         from './pages/Assessments';
import MarksEntry          from './pages/MarksEntry';
import Scoresheet          from './pages/Scoresheet';
import PassingCriteria     from './pages/PassingCriteria';
import Reports             from './pages/Reports';
import Settings            from './pages/Settings';
import ClassRegister       from './pages/ClassRegister';
import ClassSubjectAssignments from './pages/ClassSubjectAssignments';
import AuditLogs           from './pages/AuditLogs';
import Attendance          from './pages/Attendance';
import ParentPortal        from './pages/ParentPortal';
import FeeManagement       from './pages/FeeManagement';
import Disciplinary        from './pages/Disciplinary';
import Library             from './pages/Library';
import Alumni              from './pages/Alumni';
import HomeworkTracker     from './pages/HomeworkTracker';
import Bursaries           from './pages/Bursaries';
import AssetManagement     from './pages/AssetManagement';
import BoardingHouse       from './pages/BoardingHouse';
import SchoolShop          from './pages/SchoolShop';
import AnalyticsReports    from './pages/AnalyticsReports';
import NetworkManagement   from './pages/NetworkManagement';
import AdmissionsPortal    from './pages/AdmissionsPortal';
import ApplicantPortal     from './pages/ApplicantPortal';
import PrepBook            from './pages/PrepBook';
import MobileApiDocs       from './pages/MobileApiDocs';
import TransportManagement from './pages/TransportManagement';
import TeacherPerformance  from './pages/TeacherPerformance';
import SchoolRegistration  from './pages/SchoolRegistration';
import PlatformAdmin       from './pages/PlatformAdmin';
import MinistryDashboard   from './pages/MinistryDashboard';
import ParentManagement    from './pages/ParentManagement';

// ── Redirect /school/:slug → /school/:slug/login ──────────────────────────────
const SlugRedirect = () => {
  const { slug } = useParams();
  return <Navigate to={`/school/${slug}/login`} replace />;
};

// ── Protected route — redirects to the school's own login if not authed ───────
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, schoolSlug } = useAuth();
  if (!isAuthenticated) {
    const slug = schoolSlug || localStorage.getItem('sms_slug');
    return <Navigate to={slug ? `/school/${slug}/login` : '/login'} replace />;
  }
  return children;
};

// ── All school module routes (used inside /school/:slug/*) ───────────────────
function SchoolRoutes() {
  return (
    <ProtectedRoute>
      <Layout>
        <Routes>
          <Route path="dashboard"          element={<Dashboard />} />
          <Route path="students"           element={<Students />} />
          <Route path="teachers"           element={<Teachers />} />
          <Route path="subjects"           element={<Subjects />} />
          <Route path="enrolments"         element={<Enrolments />} />
          <Route path="assessments"        element={<Assessments />} />
          <Route path="marks"              element={<MarksEntry />} />
          <Route path="scoresheet"         element={<Scoresheet />} />
          <Route path="class-register"     element={<ClassRegister />} />
          <Route path="class-assignments"  element={<ClassSubjectAssignments />} />
          <Route path="audit-logs"         element={<AuditLogs />} />
          <Route path="attendance"         element={<Attendance />} />
          <Route path="parent-management"  element={<ParentManagement />} />
          <Route path="fees"               element={<FeeManagement />} />
          <Route path="disciplinary"       element={<Disciplinary />} />
          <Route path="library"            element={<Library />} />
          <Route path="alumni"             element={<Alumni />} />
          <Route path="homework"           element={<HomeworkTracker />} />
          <Route path="bursaries"          element={<Bursaries />} />
          <Route path="assets"             element={<AssetManagement />} />
          <Route path="boarding"           element={<BoardingHouse />} />
          <Route path="shop"               element={<SchoolShop />} />
          <Route path="analytics"          element={<AnalyticsReports />} />
          <Route path="network"            element={<NetworkManagement />} />
          <Route path="admissions"         element={<AdmissionsPortal />} />
          <Route path="prep-book"          element={<PrepBook />} />
          <Route path="mobile-api"         element={<MobileApiDocs />} />
          <Route path="transport"          element={<TransportManagement />} />
          <Route path="performance"        element={<TeacherPerformance />} />
          <Route path="passing-criteria"   element={<PassingCriteria />} />
          <Route path="reports"            element={<Reports />} />
          <Route path="settings"           element={<Settings />} />
          {/* Default: go to dashboard */}
          <Route path=""                   element={<Navigate to="dashboard" replace />} />
          <Route path="*"                  element={<Navigate to="dashboard" replace />} />
        </Routes>
      </Layout>
    </ProtectedRoute>
  );
}

function AppContent() {
  const { settings } = useSettings();
  const theme = createAppTheme(settings);
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <SnackbarProvider maxSnack={3} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Router>
          <Routes>
            {/* ── Platform-level (no school context) ── */}
            <Route path="/register-school"         element={<SchoolRegistration />} />
            <Route path="/platform"                element={<PlatformAdmin />} />
            <Route path="/ministry"                element={<MinistryDashboard />} />
            <Route path="/apply"                   element={<ApplicantPortal />} />

            {/* ── School-scoped parent portal ── */}
            <Route path="/school/:slug/parent/*"   element={<ParentPortal />} />
            <Route path="/school/:slug/parent"     element={<ParentPortal />} />

            {/* ── Legacy parent (no slug) ── */}
            <Route path="/parent/*"                element={<ParentPortal />} />

            {/* ── School-scoped routes ── */}
            <Route path="/school/:slug/login" element={<Login />} />
            <Route path="/school/:slug/*"     element={<SchoolRoutes />} />
            <Route path="/school/:slug"       element={<SlugRedirect />} />

            {/* ── Legacy fallback (standalone / no slug) ── */}
            <Route path="/login"              element={<Login />} />
            <Route path="/"                   element={<RootRedirect />} />
            <Route path="/*"                  element={<RootRedirect />} />
          </Routes>
        </Router>
      </SnackbarProvider>
    </ThemeProvider>
  );
}

// ── Root redirect — sends logged-in users to their school dashboard ───────────
function RootRedirect() {
  const { isAuthenticated, schoolSlug } = useAuth();
  if (isAuthenticated && schoolSlug) {
    return <Navigate to={`/school/${schoolSlug}/dashboard`} replace />;
  }
  const slug = localStorage.getItem('sms_slug');
  if (slug) return <Navigate to={`/school/${slug}/login`} replace />;
  return <Navigate to="/login" replace />;
}

export default function App() {
  return (
    <AuthProvider>
      <SettingsProvider>
        <AppContent />
      </SettingsProvider>
    </AuthProvider>
  );
}
